import { useParams, Link, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useRef, useState, useEffect, Component, type ReactNode } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { STATUS_CONFIG, type Shipment } from '../../mocks/shipments';
import { fetchShipmentById } from '../../lib/shipmentsService';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import ShipmentReceipt from './components/ShipmentReceipt';

const ShipmentMap = lazy(() => import('../../components/feature/ShipmentMap'));

// ── Map error boundary ────────────────────────────────────────────────────
interface EBState { hasError: boolean; errMsg: string }
class MapErrorBoundary extends Component<{ children: ReactNode; shipment: { origin: { city: string }; destination: { city: string } } }, EBState> {
  constructor(props: MapErrorBoundary['props']) {
    super(props);
    this.state = { hasError: false, errMsg: '' };
  }
  static getDerivedStateFromError(err: Error): EBState {
    return { hasError: true, errMsg: err.message };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-center px-8">
          <i className="ri-earth-line text-5xl text-teal-700 mb-4" />
          <p className="text-teal-400 font-semibold text-sm mb-1">Route Map Unavailable</p>
          <p className="text-gray-500 text-xs leading-relaxed max-w-xs">
            The map couldn&apos;t load for{' '}
            <strong className="text-gray-400">{this.props.shipment.origin.city}</strong> →{' '}
            <strong className="text-gray-400">{this.props.shipment.destination.city}</strong>.
            Tracking data is still fully available.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <div className="text-xs text-gray-500 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
              <div className="text-gray-400 font-semibold mb-0.5">From</div>
              <div className="text-white">{this.props.shipment.origin.city}</div>
            </div>
            <i className="ri-arrow-right-line text-gray-600" />
            <div className="text-xs text-gray-500 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
              <div className="text-gray-400 font-semibold mb-0.5">To</div>
              <div className="text-white">{this.props.shipment.destination.city}</div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

interface CountdownResult {
  days: number; hours: number; minutes: number; seconds: number; overdue: boolean;
}

function calcRemaining(estimatedDelivery: string): CountdownResult {
  const now = new Date();
  const target = new Date(`${estimatedDelivery}T23:59:59`);
  const diffMs = target.getTime() - now.getTime();
  if (!estimatedDelivery || isNaN(diffMs) || diffMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, overdue: true };
  }
  return {
    days: Math.floor(diffMs / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diffMs % (1000 * 60)) / 1000),
    overdue: false,
  };
}

export default function TrackingUpdatePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const receiptRef = useRef<HTMLDivElement>(null);
  const [showQr, setShowQr] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { settings } = useSiteSettings();
  const siteUrl = settings.siteUrl || 'https://swift-tracks.com';

  // Poll for live updates every 30 seconds
  useEffect(() => {
    if (!id) return;
    const load = async () => {
      const dbResult = await fetchShipmentById(id.toUpperCase());
      if (dbResult) {
        setShipment(dbResult);
        setNotFound(false);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [id]);

  const [countdown, setCountdown] = useState<CountdownResult>({ days: 0, hours: 0, minutes: 0, seconds: 0, overdue: false });

  useEffect(() => {
    if (!shipment || shipment.status === 'delivered') return;
    setCountdown(calcRemaining(shipment.estimatedDelivery));
    const timer = setInterval(() => { setCountdown(calcRemaining(shipment.estimatedDelivery)); }, 1000);
    return () => clearInterval(timer);
  }, [shipment?.estimatedDelivery, shipment?.status]);

  const handlePrint = () => window.print();

  const handleShare = async () => {
    const url = `${siteUrl}/track/${shipment?.id}`;
    const title = `Track shipment ${shipment?.id}`;
    const text = `Track my SwiftTrack shipment from ${shipment?.origin.city} to ${shipment?.destination.city}`;
    if (navigator.share) {
      try { await navigator.share({ title, text, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 text-sm">Loading shipment details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !shipment) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-5">
            <i className="ri-search-line text-2xl text-gray-400" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-gray-900 mb-3">Shipment Not Found</h2>
          <p className="text-gray-500 text-sm mb-6">The tracking ID <strong>{id}</strong> does not exist in our system.</p>
          <button onClick={() => navigate('/track')} className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white cursor-pointer" style={{ background: 'var(--primary)' }}>
            Back to Tracking
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const cfg = STATUS_CONFIG[shipment?.status ?? 'in_transit'] ?? STATUS_CONFIG['in_transit'];
  const isDelivered = shipment.status === 'delivered';
  const isHold = shipment.status === 'hold' || shipment.status === 'on_hold';
  const isPending = shipment.status === 'pending';
  const isBlinking = ['hold', 'on_hold', 'out_for_delivery', 'picked_up'].includes(shipment.status);

  const progressMap: Record<string, number> = { pending: 5, hold: 15, on_hold: 15, picked_up: 25, in_transit: 55, out_for_delivery: 82, delivered: 100 };
  const progressColorMap: Record<string, string> = { pending: '#f59e0b', hold: '#ef4444', on_hold: '#ef4444', picked_up: '#f59e0b', in_transit: 'var(--primary)', out_for_delivery: '#22c55e', delivered: '#16a34a' };
  const progress = progressMap[shipment.status];
  const progressColor = progressColorMap[shipment.status];

  return (
    <div className="min-h-screen bg-gray-50 no-print">
      <Navbar />

      {/* Header band */}
      <div className="pt-20 pb-8" style={{ background: 'linear-gradient(135deg, #0d2b2b 0%, var(--primary) 100%)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <Link to="/track" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-3 transition-colors">
                <i className="ri-arrow-left-s-line" />Back to Tracking
              </Link>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-heading font-bold text-2xl text-white font-mono">{shipment.id}</h1>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.color} ${cfg.blinkClass}`}>
                  {isBlinking && <span className="w-1.5 h-1.5 rounded-full bg-current animate-ping flex-shrink-0" />}
                  <i className={cfg.icon} />{cfg.label}
                </span>
              </div>
              <div className="text-white/60 text-sm mt-1">{shipment.description} · {shipment.service} · {shipment.weight} kg</div>
              {shipment.currentLocation?.city && (
                <div className="mt-2 inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-3 py-1.5">
                  <i className="ri-map-pin-2-fill text-teal-300 text-sm animate-pulse" />
                  <span className="text-white text-xs font-semibold">Currently at: {shipment.currentLocation.city}</span>
                </div>
              )}
              {shipment.note && (
                <div className="mt-3 inline-flex items-start gap-2 bg-amber-400/20 border border-amber-400/30 rounded-xl px-4 py-2.5 max-w-lg">
                  <i className="ri-information-line text-amber-300 flex-shrink-0 mt-0.5" />
                  <span className="text-amber-200 text-sm">{shipment.note}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col items-end gap-3">
              {isDelivered ? (
                <div className="flex items-center gap-3 bg-green-500/15 border border-green-400/30 rounded-2xl px-5 py-4">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-green-400 text-xl" />
                  </div>
                  <div>
                    <div className="text-green-300 text-xs font-semibold uppercase tracking-wide">Delivered</div>
                    <div className="text-white font-heading font-bold text-sm">{shipment.estimatedDelivery}</div>
                    <div className="text-green-500 text-xs mt-0.5">Package successfully delivered</div>
                  </div>
                </div>
              ) : isHold ? (
                <div className="flex items-center gap-3 bg-red-500/15 border border-red-400/30 rounded-2xl px-5 py-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-pause-circle-line text-red-400 text-xl animate-pulse" />
                  </div>
                  <div>
                    <div className="text-red-300 text-xs font-semibold uppercase tracking-wide">Delivery Paused</div>
                    <div className="text-white font-heading font-bold text-sm">Est. {shipment.estimatedDelivery}</div>
                    <div className="text-red-400 text-xs mt-0.5">Shipment on hold</div>
                  </div>
                </div>
              ) : isPending ? (
                <div className="flex items-center gap-3 bg-amber-500/15 border border-amber-400/30 rounded-2xl px-5 py-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-amber-400 text-xl animate-pulse" />
                  </div>
                  <div>
                    <div className="text-amber-300 text-xs font-semibold uppercase tracking-wide">Awaiting Pickup</div>
                    <div className="text-white font-heading font-bold text-sm">Est. {shipment.estimatedDelivery}</div>
                    <div className="text-amber-500 text-xs mt-0.5">Shipment registered — pending pickup</div>
                  </div>
                </div>
              ) : !countdown.overdue && (
                <div className="border border-white/15 rounded-2xl px-5 py-4 min-w-[220px]" style={{ background: 'rgba(255,255,255,0.07)' }}>
                  <div className="text-white/50 text-xs font-semibold uppercase tracking-wide text-center mb-3">
                    <i className="ri-time-line mr-1" />Delivery Countdown
                  </div>
                  <div className="flex items-end gap-2 justify-center">
                    {countdown.days > 0 && (
                      <>
                        <div className="text-center">
                          <div className="font-heading font-bold tabular-nums leading-none" style={{ fontSize: '3rem', color: countdown.days <= 1 ? '#22c55e' : countdown.days <= 3 ? '#f59e0b' : 'white' }}>{countdown.days}</div>
                          <div className="text-white/50 text-xs mt-1">{countdown.days === 1 ? 'day' : 'days'}</div>
                        </div>
                        <div className="text-white/30 font-bold text-2xl mb-3">:</div>
                      </>
                    )}
                    <div className="text-center"><div className="font-heading font-bold text-3xl text-white/80 tabular-nums leading-none">{String(countdown.hours).padStart(2, '0')}</div><div className="text-white/50 text-xs mt-1">hrs</div></div>
                    <div className="text-white/30 font-bold text-2xl mb-3">:</div>
                    <div className="text-center"><div className="font-heading font-bold text-3xl text-white/80 tabular-nums leading-none">{String(countdown.minutes).padStart(2, '0')}</div><div className="text-white/50 text-xs mt-1">min</div></div>
                  </div>
                  <div className="text-center mt-3 text-white/40 text-xs">Target: {shipment.estimatedDelivery}</div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 justify-end">
                <button onClick={handleShare} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white border border-white/20 transition-all cursor-pointer whitespace-nowrap" style={{ background: shareCopied ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.1)' }}>
                  <i className={shareCopied ? 'ri-check-line text-green-400' : 'ri-share-line text-base'} />{shareCopied ? 'Link Copied!' : 'Share Tracking'}
                </button>
                <button onClick={handlePrint} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-printer-line text-base" />Print Receipt
                </button>
                <button onClick={() => setShowQr((p) => !p)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-qr-code-line text-base" />{showQr ? 'Hide QR' : 'Show QR'}
                </button>
              </div>

              {showQr && (
                <div className="flex flex-col items-center gap-2 bg-white rounded-2xl p-3">
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(`${siteUrl}/track/${shipment.id}`)}&format=png`} alt={`QR for ${shipment.id}`} width={140} height={140} className="block rounded-lg" />
                  <p className="text-gray-500 text-xs font-medium">Scan to open tracking</p>
                  <p className="text-gray-400 font-mono text-xs">{shipment.id}</p>
                </div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-6">
            <div className="flex justify-between text-xs text-white/50 mb-2">
              <span>Picked Up</span><span>In Transit</span><span>Out for Delivery</span><span>Delivered</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: progressColor }} />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white" style={{ height: '480px' }}>
              <MapErrorBoundary shipment={shipment}>
                <Suspense fallback={<div className="w-full h-full flex items-center justify-center bg-gray-50"><div className="text-center"><i className="ri-map-2-line text-4xl text-gray-300 mb-3 block" /><p className="text-gray-400 text-sm">Loading map...</p></div></div>}>
                  <ShipmentMap shipment={shipment} />
                </Suspense>
              </MapErrorBoundary>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 text-sm mb-4">Shipment Parties</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'var(--primary-light)' }}>
                    <i className="ri-user-line text-sm" style={{ color: 'var(--primary)' }} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">From</div>
                    <div className="font-semibold text-gray-900 text-sm">{shipment.sender.name}</div>
                    <div className="text-gray-500 text-xs">{shipment.sender.city}, {shipment.sender.country}</div>
                  </div>
                </div>
                <div className="w-px h-4 bg-gray-100 ml-4" />
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'color-mix(in srgb, #22c55e 15%, white)' }}>
                    <i className="ri-map-pin-user-line text-sm text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">To</div>
                    <div className="font-semibold text-gray-900 text-sm">{shipment.receiver.name}</div>
                    <div className="text-gray-500 text-xs">{shipment.receiver.city}, {shipment.receiver.country}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 text-sm mb-4">Package Details</h3>
              <div className="space-y-3">
                {[
                  { label: 'Contents', value: shipment.description },
                  { label: 'Weight', value: `${shipment.weight} kg` },
                  { label: 'Dimensions', value: shipment.dimensions },
                  { label: 'Declared Value', value: `$${shipment.value.toLocaleString()}` },
                  { label: 'Service', value: shipment.service },
                  { label: 'Carrier', value: shipment.carrier },
                  ...(shipment.paymentMethod ? [{ label: 'Payment', value: shipment.paymentMethod }] : []),
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center">
                    <span className="text-gray-500 text-xs">{label}</span>
                    <span className="text-gray-900 text-xs font-semibold">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-semibold text-gray-900 text-sm">Tracking History</h3>
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />Live
                </span>
              </div>
              <div className="space-y-5">
                {[...shipment.timeline].reverse().map((event, i) => {
                  const evtCfg = STATUS_CONFIG[event.status];
                  return (
                    <div key={i} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border ${evtCfg.bg} ${evtCfg.color}`}>
                          <i className={`${evtCfg.icon} text-xs`} />
                        </div>
                        {i < shipment.timeline.length - 1 && <div className="w-px flex-1 bg-gray-100 mt-1" />}
                      </div>
                      <div className="pb-4 min-w-0">
                        <div className={`font-semibold text-xs ${evtCfg.color} mb-0.5`}>{evtCfg.label}</div>
                        <div className="text-gray-800 text-xs font-medium">{event.description}</div>
                        <div className="text-gray-400 text-xs mt-0.5">{event.location} · {event.timestamp}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {shipment.reason && (
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <div className={`rounded-xl border px-4 py-4 ${shipment.status === 'hold' ? 'bg-red-50 border-red-200' : shipment.status === 'out_for_delivery' || shipment.status === 'delivered' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border ${cfg.bg} ${cfg.color}`}>
                        <i className={`${cfg.icon} text-xs`} />
                      </div>
                      <div>
                        <div className={`font-semibold text-xs uppercase tracking-wide mb-1 ${shipment.status === 'hold' ? 'text-red-600' : shipment.status === 'delivered' || shipment.status === 'out_for_delivery' ? 'text-green-700' : 'text-amber-700'}`}>
                          Message from SwiftTrack
                        </div>
                        <p className={`text-xs leading-relaxed ${shipment.status === 'hold' ? 'text-red-800' : shipment.status === 'delivered' || shipment.status === 'out_for_delivery' ? 'text-green-800' : 'text-amber-900'}`}>{shipment.reason}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <ShipmentReceipt ref={receiptRef} shipment={shipment} />
    </div>
  );
}